import { type ColumnDef, type Table } from '@tanstack/react-table';
import React from 'react';

import {
  CLINICAL_TASK_PRIORITY_LABELS,
  CLINICAL_TASK_STATUS_LABELS,
  categoryLabel,
} from '@eluve/clinical-tasks';
import {
  type DataTableFilterField,
  Icon,
  ServerDataTable,
  SortableColumnHeader,
  Stack,
  Tag,
  Text,
  cn,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';
import {
  CLINICAL_TASK_CATEGORY_ICONS,
  CLINICAL_TASK_PRIORITY_CLASSES,
} from '@eluve/mini-apps';

import {
  type ClinicalTaskRow,
  type TableTaskStatus,
} from './fetch-clinical-task-rows';

const TASK_STATUS_CLASSES: Record<TableTaskStatus, string> = {
  draft: 'bg-bg-tertiary text-text-secondary',
  requested: 'bg-bg-info-subtle text-text-info-subtle',
  'in-progress': 'bg-bg-warning-subtle text-text-warning-strong',
  completed: 'bg-bg-positive-subtle text-text-positive-subtle',
  cancelled: 'bg-bg-negative-subtle text-text-negative-subtle',
};

const RESTRICTED_LABEL = 'Restricted';

const RestrictedPlaceholder: React.FC = () => (
  <Text inline color="supporting" className="italic">
    {RESTRICTED_LABEL}
  </Text>
);

const TaskCell: React.FC<{
  row: ClinicalTaskRow;
  onOpen?: (row: ClinicalTaskRow) => void;
}> = ({ row, onOpen }) => {
  // Multi-line description: Button/LinkButton force nowrap + fixed height
  // (would truncate). Raw button = action, not link; cf. PatientFiles cell.
  const className =
    'text-text-primary block max-w-md font-medium leading-snug whitespace-normal';
  if (!onOpen) {
    return (
      <Text inline className={className}>
        {row.description}
      </Text>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(row)}
      className={cn(className, 'text-left hover:underline')}
      data-testid="clinical-task-open"
    >
      {row.description}
    </button>
  );
};

/**
 * Column defs for the tenant Tasks table. Only Status/Priority sort (their
 * FHIR search params are indexed, alphabetically by code — not clinical rank).
 * Task, Assigned To, Created By, and Planned carry no server sort: description
 * is unindexable, the reference params would sort by opaque practitioner id
 * (not name), and the due date lives in `restriction.period`, which no Task
 * search param covers.
 */
export const buildTaskColumns = (
  onOpenTask?: (row: ClinicalTaskRow) => void,
): ColumnDef<ClinicalTaskRow>[] => [
  {
    accessorKey: 'description',
    header: 'Task',
    enableSorting: false,
    cell: ({ row }) => <TaskCell row={row.original} onOpen={onOpenTask} />,
  },
  {
    accessorKey: 'category',
    header: 'Type',
    enableSorting: false,
    cell: ({ row }) => {
      const { category } = row.original;
      return (
        <Tag
          size="s"
          className="bg-bg-tertiary text-text-secondary"
          label={categoryLabel(category)}
        >
          <Icon
            name={CLINICAL_TASK_CATEGORY_ICONS[category ?? 'general']}
            size="xs"
          />
        </Tag>
      );
    },
  },
  {
    accessorKey: 'patientName',
    header: 'For',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.patientName ? (
        <Text inline className="whitespace-nowrap privacy-text">
          {row.original.patientName}
        </Text>
      ) : (
        <RestrictedPlaceholder />
      ),
  },
  {
    accessorKey: 'ownerReference',
    header: 'Assigned to',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.ownerName ? (
        <Text inline>{row.original.ownerName}</Text>
      ) : (
        <RestrictedPlaceholder />
      ),
  },
  {
    accessorKey: 'dueDate',
    header: 'Planned',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.dueDate ? (
        <Text inline className="whitespace-nowrap">
          {formatToLocale(row.original.dueDate, { noTime: true })}
        </Text>
      ) : (
        <Text inline color="supporting">
          Not set
        </Text>
      ),
  },
  {
    accessorKey: 'requesterName',
    header: 'Created by',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.requesterName ? (
        <Text inline>{row.original.requesterName}</Text>
      ) : (
        <RestrictedPlaceholder />
      ),
  },
  {
    accessorKey: 'priority',
    header: ({ column }) => (
      <SortableColumnHeader column={column} label="Priority" />
    ),
    cell: ({ row }) => {
      const { priority } = row.original;
      if (!priority) return null;
      return (
        <Tag
          size="s"
          className={CLINICAL_TASK_PRIORITY_CLASSES[priority]}
          label={CLINICAL_TASK_PRIORITY_LABELS[priority]}
        />
      );
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <SortableColumnHeader column={column} label="Status" />
    ),
    cell: ({ row }) => (
      <Tag
        size="s"
        pill
        className={TASK_STATUS_CLASSES[row.original.status]}
        label={CLINICAL_TASK_STATUS_LABELS[row.original.status]}
      />
    ),
  },
];

export interface ClinicalTasksTableViewProps {
  /** Pre-built table (manual pagination/sorting/filtering); the container
   *  wires it to the server data hook. */
  table: Table<ClinicalTaskRow>;
  filterFields: DataTableFilterField<ClinicalTaskRow>[];
  isPending?: boolean;
  /** Drives the empty-state copy: filtered-but-empty vs nothing yet. */
  hasActiveFilters?: boolean;
}

/**
 * Presentational tasks table: toolbar (Type/Status/Priority/Assignee facets),
 * columns, server pagination. All data/state comes from the passed-in table.
 */
export const ClinicalTasksTableView: React.FC<ClinicalTasksTableViewProps> = ({
  table,
  filterFields,
  isPending = false,
  hasActiveFilters = false,
}) => (
  <Stack gap={2}>
    <ServerDataTable
      table={table}
      filterFields={filterFields}
      isPending={isPending}
      placeholder={
        hasActiveFilters
          ? 'No tasks match these filters.'
          : 'Tasks extracted from your appointments will appear here.'
      }
    />
  </Stack>
);
